import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Award, CheckCircle2, ClipboardList, Clock, MessageSquareText } from "lucide-react";
import StatCard from "@/components/shared/StatCard";
import PageHeader from "@/components/shared/PageHeader";
import EmptyState from "@/components/shared/EmptyState";
import AssessmentCard from "@/components/shared/AssessmentCard";
import RatingStars from "@/components/shared/RatingStars";
import {
  usePartnerAssessments,
  usePendingWorkLogsForAssessment,
  useSubmitAssessment,
} from "@/hooks/useAssessments";
import { TableSkeleton } from "@/components/shared/Skeletons";
import { Field } from "@/components/forms";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/partner/assessments/")({
  component: PartnerAssessmentsPage,
});

const SKILL_KEYS = ["communication", "technical", "punctuality", "teamwork"] as const;
const SKILL_LABELS: Record<string, string> = {
  communication: "Communication",
  technical: "Technical Proficiency",
  punctuality: "Punctuality",
  teamwork: "Teamwork",
};

interface AssessmentFormProps {
  open: boolean;
  onClose: () => void;
  workLog: any | null;
  onSubmit: (data: any) => void;
  submitting: boolean;
}

function AssessmentFormDialog({ open, onClose, workLog, onSubmit, submitting }: AssessmentFormProps) {
  const [rating, setRating] = useState(3);
  const [skillScores, setSkillScores] = useState<Record<string, number>>({
    communication: 3,
    technical: 3,
    punctuality: 3,
    teamwork: 3,
  });
  const [tasksCompleted, setTasksCompleted] = useState(5);
  const [onTime, setOnTime] = useState(true);
  const [qualityScore, setQualityScore] = useState(3.5);
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async () => {
    if (!feedback.trim()) return;
    await onSubmit({
      work_log_id: Number(workLog?.id),
      rating,
      skill_scores: skillScores,
      kpi_metrics: { tasks_completed: tasksCompleted, on_time: onTime, quality_score: qualityScore },
      feedback,
    });
  };

  if (!workLog) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Assess intern performance</DialogTitle>
          <DialogDescription>
            {workLog.studentName} · {workLog.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <section className="rounded-md border border-border bg-muted/40 p-4">
            <p className="eyebrow mb-3">Overall performance rating</p>
            <div className="flex justify-center">
              <RatingStars value={rating} onChange={(v) => setRating(v)} label="" />
            </div>
          </section>

          <section>
            <p className="eyebrow mb-3">Skill dimensions</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {SKILL_KEYS.map((key) => (
                <div key={key} className="rounded-md border border-border p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">{SKILL_LABELS[key]}</span>
                    <span className="font-mono text-sm text-body tabular-nums">
                      {skillScores[key]}/5
                    </span>
                  </div>
                  <div
                    className="flex gap-1"
                    role="radiogroup"
                    aria-label={`${SKILL_LABELS[key]} score`}
                  >
                    {Array.from({ length: 5 }).map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        role="radio"
                        aria-checked={skillScores[key] === i + 1}
                        aria-label={`${i + 1} of 5`}
                        onClick={() => setSkillScores((s) => ({ ...s, [key]: i + 1 }))}
                        className={cn(
                          "h-2 flex-1 rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
                          i < skillScores[key]
                            ? "bg-primary"
                            : "bg-hairline hover:bg-muted-foreground/40",
                        )}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Tasks completed">
              <Input
                type="number"
                value={tasksCompleted}
                onChange={(e) => setTasksCompleted(Number(e.target.value))}
                min={0}
              />
            </Field>
            <Field label="Quality score (1–5)">
              <Input
                type="number"
                value={qualityScore}
                onChange={(e) => setQualityScore(Number(e.target.value))}
                min={1}
                max={5}
                step={0.1}
              />
            </Field>
            <Field label="Delivery status">
              {({ id }) => (
                <Select
                  value={onTime ? "on_time" : "late"}
                  onValueChange={(v) => setOnTime(v === "on_time")}
                >
                  <SelectTrigger id={id}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="on_time">On Time</SelectItem>
                    <SelectItem value="late">Late</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
          </div>

          <Field label="Qualitative feedback" required>
            <Textarea
              placeholder="Provide detailed feedback on the intern's performance, strengths, and areas for improvement..."
              rows={4}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
            />
          </Field>
        </div>

        <DialogFooter>
          <Button onClick={onClose} variant="outline">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!feedback.trim()} loading={submitting}>
            {!submitting && <CheckCircle2 />}
            {submitting ? "Submitting..." : "Submit assessment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PartnerAssessmentsPage() {
  const [activeTab, setActiveTab] = useState<"pending" | "history">("pending");
  const [selectedWorkLog, setSelectedWorkLog] = useState<any | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: assessments = [], isLoading: assessmentsLoading } = usePartnerAssessments();
  const { data: pendingLogs = [], isLoading: pendingLoading } = usePendingWorkLogsForAssessment();
  const submitAssessment = useSubmitAssessment();

  const isLoading = assessmentsLoading || pendingLoading;
  const totalSubmitted = assessments.length;

  const handleOpenForm = (workLog: any) => {
    setSelectedWorkLog(workLog);
    setDialogOpen(true);
  };

  const handleSubmitAssessment = async (data: any) => {
    await submitAssessment.mutateAsync(data);
    setDialogOpen(false);
    setSelectedWorkLog(null);
  };

  if (isLoading) {
    return <TableSkeleton rows={5} columns={4} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Performance assessments"
        subtitle="Review intern work logs and provide structured performance feedback."
        breadcrumbs={[
          { label: "Dashboard", path: "/partner/dashboard" },
          { label: "Assessments" },
        ]}
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4">
        <StatCard label="Pending reviews" value={pendingLogs.length} icon={<Clock />} />
        <StatCard label="Submitted assessments" value={totalSubmitted} icon={<CheckCircle2 />} />
        <StatCard
          label="Avg. rating given"
          value={
            totalSubmitted > 0
              ? (assessments.reduce((s, a) => s + a.rating, 0) / totalSubmitted).toFixed(1)
              : "—"
          }
          icon={<Award />}
        />
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "pending" | "history")}>
        <TabsList>
          <TabsTrigger value="pending">Pending ({pendingLogs.length})</TabsTrigger>
          <TabsTrigger value="history">History ({totalSubmitted})</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4">
          {pendingLogs.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2">
              {pendingLogs.map((log: any) => (
                <article
                  key={log.id}
                  className="flex flex-col gap-4 rounded-md border border-border bg-card p-4 transition-colors hover:border-hairline-strong md:p-5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <Avatar className="size-9 rounded-full">
                        <AvatarFallback className="rounded-full text-xs">
                          {(log.studentName || "S").charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <h3 className="truncate text-sm font-medium text-foreground">
                          {log.studentName}
                        </h3>
                        <p className="truncate text-caption text-muted-foreground">
                          {log.description} · {log.hours}h
                        </p>
                      </div>
                    </div>
                    <Badge variant="warning" dot className="shrink-0">
                      Awaiting review
                    </Badge>
                  </div>
                  <div className="flex justify-end border-t border-border pt-4">
                    <Button size="sm" variant="outline" onClick={() => handleOpenForm(log)}>
                      <MessageSquareText />
                      Conduct assessment
                    </Button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={<CheckCircle2 />}
              title="All caught up!"
              description="No pending work logs awaiting assessment."
            />
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          {totalSubmitted > 0 ? (
            <div className="grid gap-4 md:grid-cols-2">
              {assessments.map((assessment: any) => (
                <AssessmentCard key={assessment.id} assessment={assessment} variant="full" />
              ))}
            </div>
          ) : (
            <EmptyState
              icon={<ClipboardList />}
              title="No assessments yet"
              description="Submitted performance assessments will appear here."
            />
          )}
        </TabsContent>
      </Tabs>

      <AssessmentFormDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedWorkLog(null);
        }}
        workLog={selectedWorkLog}
        onSubmit={handleSubmitAssessment}
        submitting={submitAssessment.isPending}
      />
    </div>
  );
}
